// Login page — the first screen a returning user sees.
// Orenza aesthetic: Snow background, faint Cobalt glow top-right, brand mark + Sora wordmark, single card.

import { useState, type FormEvent } from 'react';
import { Button } from '@/components/primitives/Button';
import { Input } from '@/components/primitives/Input';
import { KbdChip } from '@/components/primitives/KbdChip';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from '@/store/auth';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const body: Record<string, string> = { email: email.trim(), password };
      if (twoFactorRequired && twoFactorCode) body.twoFactorCode = twoFactorCode;
      await api.post('/auth/login', body);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'auth.2fa-required') {
          setTwoFactorRequired(true);
          setError('Enter your two-factor code to continue.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Network error. Check your connection and try again.');
      }
      setLoading(false);
      return;
    }

    // Separate try/catch so a /me failure surfaces a distinct message instead of
    // looking like a login failure. One quiet retry: right after login the old
    // (expired) session's silent-refresh can still be settling — a second /me
    // two seconds later succeeds where the first got caught in that window.
    try {
      let me;
      try {
        me = await api.get('/auth/me');
      } catch {
        await new Promise((r) => setTimeout(r, 2000));
        me = await api.get('/auth/me');
      }
      useAuthStore.getState().setAuthenticated({
        user: me.data.user,
        tenant: me.data.tenant,
        companies: me.data.companies,
        activeCompanyId: me.data.activeCompanyId,
        activeBranchId: me.data.activeBranchId,
        supplierStateCode: me.data.supplierStateCode ?? null,
      });
    } catch {
      setError('Signed in but could not load your profile — reload the page and you\'ll be in.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-snow">
      {/* Faint Cobalt + Citrus glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-40 h-[640px] w-[640px] rounded-full opacity-[0.07] blur-3xl"
        style={{ background: 'var(--color-primary)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -left-32 h-[500px] w-[500px] rounded-full opacity-[0.05] blur-3xl"
        style={{ background: 'var(--color-warm)' }}
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-[440px] flex-col items-center justify-center px-6 py-12">
        {/* Brand mark + wordmark */}
        <div className="mb-10 flex items-center gap-3">
          <img src="/brand/mark.svg" alt="" aria-hidden className="h-10 w-10" />
          <div className="flex flex-col leading-none">
            <span className="font-display text-display-sm font-semibold text-obsidian" style={{ letterSpacing: 'var(--tracking-display)' }}>
              Orenza
            </span>
            <span className="mt-0.5 text-caption text-slate">Billing Solutions</span>
          </div>
        </div>

        {/* Card — Orenza: separation by colour, not shadow */}
        <div className="w-full rounded-md border border-mist bg-frost p-8">
          <h1 className="mb-1 font-display text-display-sm font-semibold text-obsidian">Sign in</h1>
          {/* Deliberately NOT "Welcome back" — that phrase is the Gateway greeting
              and doubles as the signed-in marker for the acceptance suites. */}
          <p className="mb-6 text-body-sm text-slate">Good to see you. Continue to your workspace.</p>

          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <Input
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.in"
            />
            <Input
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
            {twoFactorRequired && (
              <Input
                label="Two-factor code"
                type="text"
                name="twoFactorCode"
                autoComplete="one-time-code"
                required
                pattern="(\d{6}|[0-9a-fA-F]{10})"
                maxLength={10}
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                placeholder="123456"
                hint="6-digit code from your authenticator app — or a 10-character recovery code"
              />
            )}

            {error && (
              <div className="rounded-md border border-crimson/20 bg-crimson/[0.05] px-3 py-2 text-body-sm text-crimson">
                {error}
              </div>
            )}

            <Button type="submit" loading={loading} size="lg" className="mt-2 w-full">
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>

            <div className="mt-4 flex items-center justify-between text-caption text-slate">
              <span>
                Press <KbdChip>Enter</KbdChip> to sign in
              </span>
              <a href="#forgot" className="text-cobalt hover:underline">
                Forgot password?
              </a>
            </div>
          </form>
        </div>

        {/* Trust strip — what makes Orenza distinctive */}
        <div className="mt-6 grid grid-cols-4 gap-3 text-center">
          {[
            { label: 'Tally\nparity', sub: 'F4–F10 keys' },
            { label: 'GST 2.0\nready', sub: '0/3/5/18/40%' },
            { label: 'Bill-wise\nAR/AP', sub: 'MSME 43B(h)' },
            { label: 'Made in\nIndia', sub: '₹ · DD-MMM-YY' },
          ].map((t) => (
            <div key={t.label} className="rounded-sm border border-mist/60 bg-white/40 px-2 py-2">
              <div className="text-nano font-medium uppercase tracking-[0.08em] text-obsidian whitespace-pre-line leading-tight">{t.label}</div>
              <div className="mt-0.5 text-nano text-cool">{t.sub}</div>
            </div>
          ))}
        </div>

        <p className="mt-6 text-center text-caption text-cool">
          Need a workspace?{' '}
          <a href="/signup" className="text-cobalt hover:underline">
            Create one
          </a>
        </p>
      </div>
    </div>
  );
}
